import { Command } from 'cadenza/api-client/workbook-commands';
import { type RootStateOr, getStore } from 'cadenza/store/cadenza-store';
import type { Primitive } from 'cadenza/utils/primitive';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getDataViewById,
  getViewById,
  getViews,
  getWorkbookNameAndHash,
  getWorksheets,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { loadExtentFromSession } from 'cadenza/workbook/workbook-session-storage';
import type { Settings } from 'cadenza/workbook/workbook-settings-util';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

export const setWorkbookSettings = (settings: Settings) => {
  return getStore()
    .dispatch(executeCommand(Command.CHANGE_WORKBOOK_SETTINGS({ settings })))
    .unwrap();
};

export function setViewSetting(viewId: ViewId, persistenceKey: string, value: unknown) {
  return setViewSettings(viewId, { [persistenceKey]: value });
}

function setViewSettings(viewId: ViewId, settings: Settings) {
  return getStore().dispatch(
    executeCommand(
      Command.CHANGE_VIEW_SETTINGS({
        viewId,
        settings,
      }),
    ),
  );
}

export function setDataViewSetting(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  persistenceKey: string,
  value: Primitive,
) {
  return getStore().dispatch(
    executeCommand(
      Command.CHANGE_DATA_VIEW_SETTINGS({
        dataViewId,
        settings: { [persistenceKey]: value },
      }),
    ),
  );
}

export function setDataViewSettings(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  newSettings: Record<string, Primitive>,
) {
  const dataViewDto = getDataViewById(state, dataViewId);
  const settings = { ...dataViewDto.settings };
  Object.assign(settings, newSettings);
  return getStore().dispatch(executeCommand(Command.CHANGE_DATA_VIEW_SETTINGS({ dataViewId, settings })));
}

/**
 * Clone view to a different worksheet
 *
 * Note: This is just a helper function. Since it's not a reducer it mustn't modify the state. Like
 * regular code, it calls selectors and dispatches a thunk.
 */
export const copyViewToWorksheet = (viewId: ViewId, targetWorksheetId: WorksheetId) => {
  const commands = [];

  // If view is a map view, synchronize map extent first
  const view = getViewById(getStore().getState(), viewId);
  if (view.type === ViewType.MAP) {
    const changeCurrentExtentCommand = createSaveMapExtentCommand(viewId, true);
    if (changeCurrentExtentCommand) {
      commands.push(changeCurrentExtentCommand);
    }
  }

  commands.push(
    Command.COPY_VIEW({
      viewId,
      targetWorksheetId,
    }),
  );

  return getStore()
    .dispatch(executeCommand(...commands))
    .unwrap();
};

/**
 * Create a new worksheet with the given print name
 */
export const addWorksheet = (printName: string) => {
  return getStore()
    .dispatch(executeCommand(Command.ADD_WORKSHEET({ printName })))
    .unwrap()
    .then(() => {
      const worksheets = getWorksheets(getStore().getState());
      // last one is the new worksheet
      return worksheets[worksheets.length - 1].id;
    });
};

/**
 * Creates duplicate of given worksheet
 *
 * @param printName - print name of the new worksheet to be created
 * @param sourceWorksheetId - id of the source worksheet
 * @return executeCommand thunk
 */
export const duplicateWorksheet = (printName: string, sourceWorksheetId: WorksheetId) => {
  const copyWorksheetCommand = Command.COPY_WORKSHEET({ printName, sourceWorksheetId });
  return getStore()
    .dispatch(executeCommand(copyWorksheetCommand))
    .unwrap()
    .then(() => {
      const worksheets = getWorksheets(getStore().getState());
      // duplicated worksheet is always placed after the original
      const sourceWorksheetIndex = worksheets.findIndex(({ id }) => id === sourceWorksheetId);
      return worksheets[sourceWorksheetIndex + 1].id;
    });
};

/**
 * Save current extent for all map views in the workbook without triggering on demand overlays
 *
 * @return Promise of the new workbook state or void, if there was no map views
 */
export const saveMapViewsExtentSilently = async () => {
  const mapViews = getViews(getStore().getState()).filter((view) => view.type === ViewType.MAP);
  const commands = mapViews.map(({ id: viewId }) => createSaveMapExtentCommand(viewId, true)).filter((c) => !!c);
  if (commands.length > 0) {
    return await getStore()
      .dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })))
      .unwrap();
  }
};

export function createSaveMapExtentCommand(viewId: ViewId, ignoreInOnDemand = false) {
  const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  const mapExtent = loadExtentFromSession(workbookNameAndHash.id, viewId);
  if (mapExtent) {
    return Command.CHANGE_CURRENT_EXTENT({
      viewId,
      currentExtent: mapExtent,
      ignoreInOnDemand,
    });
  }
}

export function reorderLayers(viewId: ViewId, newLayerOrder: LayerId[], parentLayerId?: LayerId) {
  return getStore().dispatch(
    executeCommand(
      Command.REORDER_LAYERS({
        viewId,
        newLayerOrder,
        parentLayerId,
      }),
    ),
  );
}

export function reorderDataViews(viewId: ViewId, newDataViewOrder: DataViewId[]) {
  return getStore().dispatch(
    executeCommand(
      Command.REORDER_DATA_VIEWS({
        viewId,
        newDataViewOrder,
      }),
    ),
  );
}
